import { DndContext, closestCenter } from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import classcat from "classcat";
import React, { useMemo } from "react";

import { IconPlusCircle, Spinner } from "@touchtech/web-baselayer-ui";

import CircularThumbnails from "@is/components/CircularThumbnails";
import CollectionModal from "@is/components/CollectionModal";
import DrawerActionButton, {
  ActionButtonVariant,
} from "@is/components/DrawerActionButton/DrawerActionButton";
import RemoveCollectionModal from "@is/components/RemoveCollectionModal";

import CollectionItem from "../CollectionItem";
import AllItemsView from "./AllItemsView";
import CollectionEditView from "./CollectionEditView";
import css from "./StoreFavoritesView.module.css";
import useManageStoreFavoritesView from "./hooks/useManageStoreFavoritesView";

const StoreFavoritesView = () => {
  const {
    t,
    isListOfCollectionsView,
    isCollectionEditView,
    collections,
    collectionName,
    currentCollection,
    isAllViewOpened,
    isCollectionModalOpen,
    isRemoveModalOpen,
    isLoading,
    handleGoBack,
    handleCollectionModalOpen,
    handleCollectionModalClose,
    handleNameChange,
    handleCollectionSave,
    handleCollectionSelect,
    handleCurrentCollectionClose,
    handleRemoveCollectionModalOpen,
    handleRemoveCollectionModalClose,
    handleCollectionRemove,
    handleAllViewOpen,
    handleAllViewClose,
    getAllCollectionItems,
    handleCollectionsReorder,
  } = useManageStoreFavoritesView();

  // Every real collection, in the order the server merged them — brand-wide rows already sit
  // at the position the admin dashboard chose.
  const listedCollections = useMemo(() => {
    return collections?.items.filter((c) => !!c.id) || [];
  }, [collections]);

  // Only store-owned rows are draggable. Brand-wide rows render inside the same list but are
  // never registered with dnd-kit, so they hold their position while store staff reorder
  // around them.
  const sortableIds = useMemo(() => {
    return listedCollections.filter((c) => !c.isBrandWide).map((c) => c.id);
  }, [listedCollections]);

  const allItemsCollection = useMemo(() => {
    return collections?.items.find((c) => !c.id);
  }, [collections]);

  return (
    <>
      {isListOfCollectionsView && (
        <div className={css.container}>
          <DrawerActionButton
            onSecondaryButtonClick={handleGoBack}
            secondaryVariant={ActionButtonVariant.Back}
          />
          <div className={css.headerName}>
            <h1 className="text-headline-5xl">{t("Store favorites")}</h1>
          </div>
          <div>
            <div className={css.content}>
              <div>
                <div className={classcat(["text-label-lg", css.subtitle])}>
                  {t("Your collections")}
                </div>
                {!collections && isLoading && (
                  <div className={css.spinnerContainer}>
                    <Spinner className="text-headline-5xl" />
                  </div>
                )}

                {collections && !isLoading && (
                  <div className={css.toolsContainer}>
                    {allItemsCollection && (
                      <CollectionItem
                        key="all-items"
                        title={t("All items")}
                        description={`${allItemsCollection.totalItems} ${
                          allItemsCollection.totalItems !== 1
                            ? t("items")
                            : t("item")
                        }`}
                        onClick={handleAllViewOpen}
                        icon={
                          <CircularThumbnails
                            thumbnails={allItemsCollection.thumbnails}
                            totalItems={allItemsCollection.totalItems}
                          />
                        }
                      />
                    )}

                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleCollectionsReorder}
                      modifiers={[restrictToParentElement]}
                    >
                      <SortableContext
                        items={sortableIds}
                        strategy={rectSortingStrategy}
                      >
                        {listedCollections.map((collection) => (
                          <CollectionItem
                            key={collection.id}
                            id={collection.id}
                            sortableId={
                              collection.isBrandWide ? undefined : collection.id
                            }
                            isBrandWide={collection.isBrandWide}
                            title={collection.name}
                            description={`${collection.totalItems} ${
                              collection.totalItems !== 1
                                ? t("items")
                                : t("item")
                            }`}
                            onClick={handleCollectionSelect}
                            showDragHandle={
                              !collection.isBrandWide && sortableIds.length > 1
                            }
                            icon={
                              <CircularThumbnails
                                thumbnails={collection.thumbnails}
                                totalItems={collection.totalItems}
                              />
                            }
                          />
                        ))}
                      </SortableContext>
                    </DndContext>

                    <CollectionItem
                      title={t("Create collection")}
                      description={t("Add new collection")}
                      onClick={handleCollectionModalOpen}
                      whiteBg={false}
                      icon={
                        <div
                          className={classcat([
                            "text-headline-4xl",
                            css.iconContainer,
                          ])}
                        >
                          <IconPlusCircle />
                        </div>
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {isCollectionEditView ? (
        <CollectionEditView
          collectionId={currentCollection.id}
          onClose={handleCurrentCollectionClose}
          onRemove={handleRemoveCollectionModalOpen}
        />
      ) : null}
      {isAllViewOpened && (
        <AllItemsView
          onClose={handleAllViewClose}
          onGetAllItems={getAllCollectionItems}
        />
      )}
      <CollectionModal
        title={t("Create collection")}
        isOpen={isCollectionModalOpen}
        onClose={handleCollectionModalClose}
        value={collectionName}
        onChange={handleNameChange}
        onPrimaryButtonClick={handleCollectionSave}
        onSecondaryButtonClick={handleCollectionModalClose}
        isEditMode={false}
        hasNameDifference={true}
      />
      <RemoveCollectionModal
        isOpen={isRemoveModalOpen}
        onClose={handleRemoveCollectionModalClose}
        onPrimaryButtonClick={handleCollectionRemove}
        onSecondaryButtonClick={handleRemoveCollectionModalClose}
      />
    </>
  );
};

export default StoreFavoritesView;
